/**
 * <plakkie-quote-form>
 *
 * Enhances the Plakkie quote form (Shopify contact form):
 * - Estimated pairs slider over a stepped list (data-steps), mirrored into a hidden contact field.
 * - Pre-selects an "I'm ordering for" chip from any [data-plakkie-preselect] link on the page,
 *   or from ?ordering_for=<label> in the URL.
 * - Shows the design sent over by <plakkie-customiser> (hidden contact[Design] field).
 * - Client-side validation with inline messages; the native required attributes remain.
 */

const DESIGN_EVENT = 'plakkie:design';
const DESIGN_STORAGE_KEY = 'plakkie:design';
const numberFormat = new Intl.NumberFormat('en-ZA');

class PlakkieQuoteForm extends HTMLElement {
  /** @type {number[]} */
  #steps = [];
  #pairsBound = false;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.form.noValidate = true;
    this.form.addEventListener('submit', this.#handleSubmit);
    this.form.addEventListener('input', this.#handleInput);
    this.form.addEventListener('change', this.#handleInput);

    this.#setupPairs();
    this.#setupDesign();
    this.#preselectFromUrl();
    this.#showSent();

    document.addEventListener(DESIGN_EVENT, this.#handleDesignEvent);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.form?.removeEventListener('input', this.#handleInput);
    this.form?.removeEventListener('change', this.#handleInput);
    document.removeEventListener(DESIGN_EVENT, this.#handleDesignEvent);
  }

  /* Pairs slider ------------------------------------------------------------------------------ */

  #setupPairs() {
    const range = this.querySelector('[data-plakkie-pairs-range]');
    if (!(range instanceof HTMLInputElement)) return;

    this.#steps = (this.dataset.steps || '')
      .split(',')
      .map((value) => parseInt(value.trim(), 10))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!this.#steps.length) this.#steps = [50, 100, 500, 1000, 5000];

    const start = parseInt(this.dataset.defaultPairs || '', 10);
    let index = this.#steps.indexOf(start);
    if (index < 0) index = 0;

    range.max = String(this.#steps.length - 1);
    range.value = String(index);

    const min = this.querySelector('[data-plakkie-pairs-min]');
    const max = this.querySelector('[data-plakkie-pairs-max]');
    if (min) min.textContent = this.#formatPairs(0, false);
    if (max) max.textContent = this.#formatPairs(this.#steps.length - 1, false);

    if (!this.#pairsBound) {
      range.addEventListener('input', () => this.#renderPairs(range));
      this.#pairsBound = true;
    }
    this.#renderPairs(range);
  }

  /**
   * @param {number} index
   * @param {boolean} [withSuffix]
   */
  #formatPairs(index, withSuffix = true) {
    const last = index === this.#steps.length - 1;
    const value = `${numberFormat.format(this.#steps[index])}${last ? '+' : ''}`;
    return withSuffix ? `${value} ${this.dataset.pairsSuffix || ''}`.trim() : value;
  }

  /** @param {HTMLInputElement} range */
  #renderPairs(range) {
    const index = Number(range.value);
    const label = this.#formatPairs(index);
    const output = this.querySelector('[data-plakkie-pairs-output]');
    const field = this.querySelector('[data-plakkie-pairs-field]');

    if (output) output.textContent = label;
    if (field instanceof HTMLInputElement) field.value = this.#formatPairs(index, false);
    range.setAttribute('aria-valuetext', label);

    const fill = this.#steps.length > 1 ? (index / (this.#steps.length - 1)) * 100 : 0;
    range.style.setProperty('--plakkie-range-fill', `${fill}%`);
  }

  /* Sent confirmation ------------------------------------------------------------------------- */

  /** After Shopify reloads the page with ?contact_posted=true, bring the thank-you message into view. */
  #showSent() {
    const success = this.querySelector('[data-plakkie-success]');
    if (!(success instanceof HTMLElement) || success.hidden) return;

    try {
      sessionStorage.removeItem(DESIGN_STORAGE_KEY);
    } catch {
      // Nothing stored.
    }

    requestAnimationFrame(() => {
      success.scrollIntoView({ block: 'center' });
      success.focus({ preventScroll: true });
    });

    this.querySelector('[data-plakkie-again]')?.addEventListener('click', () => {
      success.hidden = true;
      const url = new URL(window.location.href);
      url.searchParams.delete('contact_posted');
      history.replaceState(null, '', url);
      this.querySelector('[data-plakkie-chip], input:not([type="hidden"])')?.focus();
    });
  }

  /* "I'm ordering for" pre-selection -------------------------------------------------------- */

  /** @param {string} label */
  selectChip(label) {
    const wanted = label.trim().toLowerCase();
    const chip = [...this.querySelectorAll('[data-plakkie-chip]')].find(
      (input) => input instanceof HTMLInputElement && input.value.trim().toLowerCase() === wanted
    );
    if (!(chip instanceof HTMLInputElement)) return false;

    chip.checked = true;
    chip.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  #preselectFromUrl() {
    const value = new URLSearchParams(window.location.search).get('ordering_for');
    if (value) this.selectChip(value);
  }

  /* Customiser design ------------------------------------------------------------------------ */

  #setupDesign() {
    const field = this.querySelector('[data-plakkie-design-field]');
    if (!(field instanceof HTMLInputElement)) return;

    if (!field.value) {
      try {
        field.value = sessionStorage.getItem(DESIGN_STORAGE_KEY) || '';
      } catch {
        // Storage unavailable; the customiser writes the field directly on this page.
      }
    }

    this.querySelector('[data-plakkie-design-clear]')?.addEventListener('click', () => {
      field.value = '';
      try {
        sessionStorage.removeItem(DESIGN_STORAGE_KEY);
      } catch {
        // Nothing stored.
      }
      this.#renderDesign();
    });

    this.#renderDesign();
  }

  #handleDesignEvent = () => this.#renderDesign();

  #renderDesign() {
    const field = this.querySelector('[data-plakkie-design-field]');
    const note = this.querySelector('[data-plakkie-design-note]');
    const text = this.querySelector('[data-plakkie-design-text]');
    if (!(field instanceof HTMLInputElement) || !(note instanceof HTMLElement)) return;

    note.hidden = !field.value;
    if (text) text.textContent = field.value;
  }

  /* Validation -------------------------------------------------------------------------------- */

  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (target.getAttribute('aria-invalid') === 'true' || target.matches('[data-plakkie-chip]')) {
      this.#validateField(target);
    }
  };

  /** @param {HTMLInputElement | HTMLTextAreaElement} field */
  #validateField(field) {
    if (field instanceof HTMLInputElement && field.matches('[data-plakkie-chip]')) {
      const checked = this.querySelector('[data-plakkie-chip]:checked');
      return this.#setError(field, checked ? '' : this.dataset.errorChip || '');
    }

    let message = '';
    if (field.validity.valueMissing) message = this.dataset.errorRequired || '';
    else if (field.validity.typeMismatch && field.type === 'email') message = this.dataset.errorEmail || '';
    else if (!field.validity.valid) message = field.validationMessage;

    return this.#setError(field, message);
  }

  /**
   * Shows or clears the inline error for a field. Returns true when the field is valid.
   * @param {HTMLElement} field
   * @param {string} message
   */
  #setError(field, message) {
    const container = field.closest('.plakkie-quote__field');
    const error = container?.querySelector('[data-plakkie-field-error]');
    const targets = field.matches('[data-plakkie-chip]')
      ? [...this.querySelectorAll('[data-plakkie-chip]')]
      : [field];

    if (error instanceof HTMLElement) {
      if (!error.id) error.id = `${field.id || 'plakkie-field'}-error`;
      error.textContent = message;
      error.hidden = !message;
    }

    for (const target of targets) {
      if (message) {
        target.setAttribute('aria-invalid', 'true');
        if (error) target.setAttribute('aria-describedby', error.id);
      } else {
        target.removeAttribute('aria-invalid');
        target.removeAttribute('aria-describedby');
      }
    }

    return !message;
  }

  #validateAll() {
    const fields = [
      ...this.form.querySelectorAll('input[required]:not([data-plakkie-chip]), textarea[required], input[type="email"], input[type="date"]'),
    ];
    const firstChip = this.form.querySelector('[data-plakkie-chip]');

    /** @type {HTMLElement | null} */
    let firstInvalid = null;

    if (firstChip instanceof HTMLInputElement && !this.#validateField(firstChip)) firstInvalid = firstChip;
    for (const field of fields) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      if (!this.#validateField(field) && !firstInvalid) firstInvalid = field;
    }

    return firstInvalid;
  }

  /* Submit ------------------------------------------------------------------------------------ */

  #handleSubmit = (event) => {
    const firstInvalid = this.#validateAll();
    if (firstInvalid) {
      // Shopify's storefront captcha listens for submit on document and re-submits the form
      // without checking defaultPrevented, so an invalid form must not reach it.
      event.preventDefault();
      event.stopPropagation();
      firstInvalid.focus();
      return;
    }

    // Valid: let the native Shopify contact form post (captcha flow included).
  };
}

if (!customElements.get('plakkie-quote-form')) {
  customElements.define('plakkie-quote-form', PlakkieQuoteForm);
}

/* Pre-selecting a chip from elsewhere on the page:
   - any link with data-plakkie-preselect="School" (audience cards); its own #quote anchor scrolls;
   - any link to #quote=<label>, e.g. "#quote=Hotel & resort" (usable from native Horizon buttons).
   Registered once per page. */
const QUOTE_HASH = /^#quote=(.+)$/;

/** @param {string} label */
function preselect(label) {
  for (const form of document.querySelectorAll('plakkie-quote-form')) {
    if (form instanceof PlakkieQuoteForm) form.selectChip(label);
  }
}

/** @param {string} hash */
function handleQuoteHash(hash) {
  const match = QUOTE_HASH.exec(hash);
  if (!match) return false;
  preselect(decodeURIComponent(match[1]));
  const target = document.getElementById('quote') || document.querySelector('plakkie-quote-form');
  target?.scrollIntoView({ block: 'start' });
  history.replaceState(null, '', `${location.pathname}${location.search}#quote`);
  return true;
}

if (!window.__plakkiePreselectBound) {
  window.__plakkiePreselectBound = true;

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element && event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const label = link.getAttribute('data-plakkie-preselect');
    if (label) {
      preselect(label);
      return;
    }

    const href = link.getAttribute('href') || '';
    const hash = href.slice(href.indexOf('#'));
    if (href.includes('#') && link.pathname === location.pathname && handleQuoteHash(hash)) {
      event.preventDefault();
    }
  });

  if (QUOTE_HASH.test(location.hash)) {
    customElements.whenDefined('plakkie-quote-form').then(() => handleQuoteHash(location.hash));
  }
}
